import { carriersGetCarriersOptions, useHeyApi } from "@platform/api";
import { useRfpCarriers, type RfpCarriersResponse } from "@platform/hooks";
import { useQuery } from "@tanstack/react-query";

export interface Carrier {
  id: string;
  name: string;
}

interface UseCarriersOptions {
  rfpId: string | undefined;
  enabled?: boolean;
}

interface UseCarriersResult {
  carriers: Carrier[];
  isLoading: boolean;
  error: Error | null;
}

const toScopedCarriers = (data: RfpCarriersResponse): Carrier[] =>
  [
    ...(data.connected ?? []),
    ...(data.unconnected ?? []),
    ...(data.unknown ?? []),
  ]
    .flatMap((carrier) =>
      carrier.shareIdentifier
        ? [{ id: carrier.shareIdentifier, name: carrier.name }]
        : [],
    )
    .sort((a, b) => a.name.localeCompare(b.name));

export const useCarriers = ({
  rfpId,
  enabled = true,
}: UseCarriersOptions): UseCarriersResult => {
  const isRfpScoped = !!rfpId;
  const { centroPortalClient } = useHeyApi();

  const rfpCarriers = useRfpCarriers<Carrier[]>({
    rfpId: rfpId ?? "",
    enabled: enabled && isRfpScoped,
    select: toScopedCarriers,
  });

  const allCarriers = useQuery({
    ...carriersGetCarriersOptions({ client: centroPortalClient }),
    enabled: enabled && !isRfpScoped,
    staleTime: 5 * 60 * 1000,
    select: (response) =>
      [...response.carriers].sort((a, b) => a.name.localeCompare(b.name)),
  });

  const { data, isLoading, error } = isRfpScoped ? rfpCarriers : allCarriers;

  return {
    carriers: isRfpScoped
      ? [...(data ?? []), { id: "other", name: "Other" }]
      : (data ?? []),
    isLoading,
    error: error instanceof Error ? error : null,
  };
};
